import {
  collection,
  firestore,
  onSnapshot,
  query,
  where,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannelMessageBookmark } from '@ps/models/lib/community-channel';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { appDateToIso } from '../utils/appDate';

export interface MyChannelBookmark {
  id: string;
  channelId: string;
  messageId: string;
  createdAt: string;
  /**
   * The message snapshot embedded on the bookmark doc. The panel renders this
   * directly — no per-row fetch — so bookmarked messages appear instantly (and
   * a since-deleted one still reads as its "deleted" placeholder), matching
   * upstream.
   */
  message: ExtendedCommunityChannelMessage;
}

/**
 * Subscribe to the current user's bookmarks for a single channel, in the order
 * Firestore returns them (NOT re-sorted — matching upstream, which renders the
 * raw query order). The query filters by `channelId` only: adding
 * `orderBy('createdAt')` alongside the equality filter would require an
 * unprovisioned composite index (the "query requires an index" error), and
 * upstream never orders server-side either.
 */
export function subscribeToMyChannelBookmarks(
  userId: string,
  channelId: string,
  onChange: (bookmarks: MyChannelBookmark[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = collection(
    db,
    'privateProfiles',
    userId,
    'communityChannelBookmarks',
  );
  const q = query(ref, where('channelId', '==', channelId));

  const unsubscribe = onSnapshot(
    q,
    snapshot => {
      const bookmarks: MyChannelBookmark[] = [];
      snapshot.forEach(doc => {
        const data = snapshotToDataWithId<CommunityChannelMessageBookmark>(doc);
        if (!data?.message) return;
        bookmarks.push({
          id: data.id,
          channelId: data.channelId,
          messageId: data.messageId,
          createdAt: appDateToIso(data.createdAt) ?? '',
          message: {
            ...data.message,
            id: data.messageId,
            channelId: data.channelId,
          },
        });
      });
      onChange(bookmarks);
    },
    error => onError?.(error),
  );
  return unsubscribe;
}
